import { Store, Action } from '@openfin/predux';

import { StoredNotification } from '../model/StoredNotification';
import { StoredApplication } from '../model/Environment';
import { ToggleFilter } from '../utils/ToggleFilter';
import { getSourceTitle, getStreamTitle } from '../view/utils/SourceInfo';
import { ActionNoopType } from '../../client/actions';
import { NotificationStream } from '../../client';
import { StoredStream } from '../model/StoredStream';

import { RootState } from './State';
import { SetSearchMode } from './partials/Search';
import {
  insertNotificationIntoSortedMaps,
  removeNotificationFromSortedMaps,
} from '../utils/sortNotifications';
import { UpdatableNotificationInternal } from '../../client/internal';

export enum ToggleCenterVisibilitySource {
  API,
  TRAY,
  BUTTON,
  INTERNAL,
}

export class CreateNotification extends Action<RootState> {
  private _notification: StoredNotification;

  public get notification(): StoredNotification {
    return this._notification;
  }

  constructor(notification: StoredNotification) {
    super();
    this._notification = notification;
  }

  public reduce(state: RootState): RootState {
    const { notification } = this;
    const notifications: StoredNotification[] = state.notifications.slice();

    // All notification IDs must be unique. The custom dispatch logic of the `CreateNotification` event should
    // ensure this, but to avoid significant side-effects, also adding a sanity check here.
    const index: number = state.notifications.findIndex((n) => n.id === notification.id);
    if (index >= 0) {
      // Replace existing notification with this ID
      console.warn(
        `Attempted to add a notification with duplicate id '${notification.id}'. Will replace existing notification.`
      );
      notifications[index] = notification;
    } else {
      // Add new notification (ordering within array doesn't matter)
      notifications.push(notification);
    }

    const stickyNotifications: StoredNotification[] = state.stickyNotifications.slice();
    if (notification.notification.sticky === 'sticky') {
      const stickyIndex: number = stickyNotifications.findIndex((n) => n.id === notification.id);
      if (stickyIndex >= 0) {
        stickyNotifications[stickyIndex] = notification;
      } else {
        stickyNotifications.push(notification);
      }
    }

    const newSortedNotificationMaps = insertNotificationIntoSortedMaps(
      state.sortedNotifications,
      notification
    );

    return {
      ...state,
      notifications,
      stickyNotifications,
      sortedNotifications: newSortedNotificationMaps,
    };
  }

  public async call(store: Store<RootState>, complete: () => Promise<void>): Promise<void> {
    const notification = this.notification;
    const promises = [];
    this._notification = {
      ...this.notification,
      title:
        getStreamTitle(store.state.streams, notification.notification.stream?.id) ||
        getSourceTitle(this.notification.source, store.state.applications),
    };
    // First remove any existing notifications with this ID, to ensure ID uniqueness
    // Should only ever be at-most one notification with this ID, using filter over find as an additional sanity check
    const existingNotifications = store.state.notifications.filter((x) => x.id === notification.id);
    if (existingNotifications.length) {
      promises.push(store.dispatch(new RemoveNotifications(existingNotifications)));
    }
    promises.push(complete());
    await Promise.all(promises);
  }
}

export class UpdateNotification extends Action<RootState> {
  private _updatedNotification: StoredNotification | null = null;
  constructor(public readonly updatePayload: UpdatableNotificationInternal) {
    super();
  }

  public get updatedNotification(): StoredNotification | null {
    return this._updatedNotification;
  }

  public reduce(state: RootState): RootState {
    const notificationIdx = state.notifications.findIndex(
      (nfn) => nfn.notification.id === this.updatePayload.id
    );

    if (notificationIdx === -1) {
      /**
       * this should never happen as validation happens when it hits client
       */
      console.warn(
        `Unable to find notification to update in store with id: ${this.updatePayload.id}`
      );
      return state;
    }

    const existingNfn = state.notifications[notificationIdx];
    const existingNfnOptions = existingNfn.notification;
    const updatedNotifications: StoredNotification[] = [...state.notifications];
    const { updatePayload } = this;

    const templateUpdate = () => {
      if (existingNfnOptions.template === 'list' && updatePayload.template === 'list') {
        return {
          list: updatePayload.list ?? existingNfnOptions.list,
        };
      } else if (
        existingNfnOptions.template === 'markdown' &&
        updatePayload.template === 'markdown'
      ) {
        return {
          body: updatePayload.body ?? existingNfnOptions.body,
        };
      } else if (existingNfnOptions.template === 'custom' && updatePayload.template === 'custom') {
        return {
          templateData: updatePayload.templateData ?? existingNfnOptions.templateData,
        };
      }
      return {};
    };

    const updatedNfn = (this._updatedNotification = updatedNotifications[notificationIdx] = {
      ...existingNfn,
      bodyText: updatePayload.bodyText ?? existingNfn.bodyText,
      notification: {
        ...existingNfnOptions,
        buttons: updatePayload.buttons ?? existingNfnOptions.buttons,
        customData: updatePayload.customData ?? existingNfnOptions.customData,
        ...templateUpdate(),
      },
      modifiers: {
        ...existingNfn.modifiers,
        revision: (existingNfn.modifiers?.revision ?? 0) + 1,
      },
    });

    return {
      ...state,
      notifications: updatedNotifications,
      stickyNotifications:
        existingNfnOptions.sticky === 'sticky'
          ? state.stickyNotifications.map((sticky) =>
              sticky.id === existingNfn.id ? updatedNfn : sticky
            )
          : state.stickyNotifications,
      sortedNotifications: insertNotificationIntoSortedMaps(state.sortedNotifications, updatedNfn),
    };
  }
}

export class RemoveNotifications extends Action<RootState> {
  constructor(public readonly notifications: StoredNotification[]) {
    super();
  }

  public reduce(state: RootState): RootState {
    const { notifications } = this;
    const idsToRemove = notifications.map((n) => {
      return n.id;
    });

    let stickyNotifications = state.stickyNotifications.slice();
    notifications.forEach((notification) => {
      if (notification.notification.sticky === 'sticky') {
        stickyNotifications = stickyNotifications.filter((n) => n.id !== notification.id);
      }
    });

    let sortedNotifications = { ...state.sortedNotifications };

    for (const nft of notifications) {
      sortedNotifications = removeNotificationFromSortedMaps(sortedNotifications, nft);
    }

    return {
      ...state,
      notifications: state.notifications.filter((n) => idsToRemove.indexOf(n.id) === -1),
      stickyNotifications,
      search: {
        ...state.search,
        results: state.search.results.filter(({ item: n }) => idsToRemove.indexOf(n.id) === -1),
      },
      sortedNotifications,
    };
  }
}

export class ClickNotification extends Action<RootState> {
  constructor(public readonly notification: StoredNotification) {
    super();
  }

  public async call(store: Store<RootState>, complete: () => Promise<void>): Promise<void> {
    if (this.notification.notification.onSelect?.__NOOP__ === ActionNoopType.EVENT_DISMISS) {
      return;
    }

    await Promise.all([complete(), store.dispatch(new RemoveNotifications([this.notification]))]);
  }
}

export class ClickButton extends Action<RootState> {
  constructor(
    public readonly notification: StoredNotification,
    public readonly buttonIndex: number
  ) {
    super();
  }

  public async call(store: Store<RootState>, complete: () => Promise<void>): Promise<void> {
    await Promise.all([complete(), store.dispatch(new RemoveNotifications([this.notification]))]);
  }
}

export class ToggleCenterVisibility extends Action<RootState> {
  constructor(
    public readonly source: ToggleCenterVisibilitySource,
    public readonly visible?: boolean
  ) {
    super();
  }

  public async call(store: Store<RootState>, complete: () => Promise<void>): Promise<void> {
    // If dispatch was programmatically called by the service always perform action
    if (this.source === ToggleCenterVisibilitySource.INTERNAL) {
      await complete();
    } else if (toggleFilter.recordToggle(this.source)) {
      await complete();
    }
    await store.dispatch(new SetSearchMode(false));
  }

  public reduce(state: RootState): RootState {
    const centerVisible = this.visible !== undefined ? this.visible : !state.centerVisible;

    return {
      ...state,
      centerVisible,
    };
  }
}

export class BlurCenter extends Action<RootState> {
  public async call(store: Store<RootState>, complete: () => Promise<void>): Promise<void> {
    // TODO: We only need to check `recordBlur` here due to spurious blur events generated from windows in a different runtime. Investigate
    // properly [SERVICE-614]
    if (toggleFilter.recordBlur()) {
      await complete();
    }
  }

  public reduce(state: RootState): RootState {
    return {
      ...state,
      centerVisible: false,
    };
  }
}

export class ToggleCenterLocked extends Action<RootState> {
  public reduce(state: RootState): RootState {
    return {
      ...state,
      centerLocked: !state.centerLocked,
    };
  }
}

export class ToggleCenterMuted extends Action<RootState> {
  public reduce(state: RootState): RootState {
    return {
      ...state,
      centerMuted: !state.centerMuted,
    };
  }
}

export class RegisterApplication extends Action<RootState> {
  constructor(public readonly application: StoredApplication) {
    super();
  }

  public reduce(state: RootState): RootState {
    const existingApp = state.applications.get(this.application.id);
    // Update notifications from this apps `title`
    let notifications: StoredNotification[] = state.notifications;
    if (existingApp && existingApp.title !== this.application.title) {
      notifications = state.notifications.map((n) => ({ ...n, title: this.application.title }));
    }
    // Preserve mute and toast persistence states
    const info = {
      ...this.application,
      muted: existingApp ? existingApp.muted : this.application.muted,
      persistToasts: existingApp ? existingApp.persistToasts : this.application.persistToasts,
    };
    const map = new Map(state.applications);
    map.set(info.id, info);
    return {
      ...state,
      applications: map,
      notifications,
    };
  }
}

export class RegisterStream extends Action<RootState> {
  private _appId: string;

  constructor(public readonly stream: NotificationStream, appId: string) {
    super();
    this._appId = appId;
  }

  public reduce(state: RootState): RootState {
    const existingStream = state.streams.get(this.stream.id);
    const streams = new Map(state.streams);

    streams.set(this.stream.id, {
      ...this.stream,
      muted: existingStream ? existingStream.muted : false,
      appId: this._appId,
    });

    return {
      ...state,
      streams,
    };
  }
}

export class ExpireNotification extends Action<RootState> {
  constructor(public readonly notification: StoredNotification) {
    super();
  }

  public async call(store: Store<RootState>, complete: () => Promise<void>): Promise<void> {
    await Promise.all([complete(), store.dispatch(new RemoveNotifications([this.notification]))]);
  }
}

export class UpdateAppMuteState extends Action<RootState> {
  constructor(public readonly application: StoredApplication, public readonly muted: boolean) {
    super();
  }

  public reduce(state: RootState): RootState {
    const info = this.application;
    const map = new Map(state.applications);
    map.set(info.id, { ...info, muted: this.muted });
    return {
      ...state,
      applications: map,
    };
  }
}

export class UpdateStreamMuteState extends Action<RootState> {
  constructor(public readonly stream: StoredStream, public readonly muted: boolean) {
    super();
  }

  public reduce(state: RootState): RootState {
    const s = this.stream;
    const map = new Map(state.streams);
    map.set(s.id, { ...s, muted: this.muted });
    return {
      ...state,
      streams: map,
    };
  }
}

export class UpdateAppToastPersistState extends Action<RootState> {
  constructor(
    public readonly application: StoredApplication,
    public readonly persistToasts: boolean
  ) {
    super();
  }

  public reduce(state: RootState): RootState {
    const info = this.application;
    const map = new Map(state.applications);
    map.set(info.id, { ...info, persistToasts: this.persistToasts });
    return {
      ...state,
      applications: map,
    };
  }
}

const toggleFilter = new ToggleFilter();
